import React from "react";
import { productUpdatesRequest } from "../lib/productUpdatesApi.js";
import ProductUpdateImageEditor from "./ProductUpdateImageEditor.jsx";
import ConfirmationModal from "./ConfirmationModal.jsx";

const contentItems = content => content.sections.flatMap(section => section.items.map(item => ({ ...item, subject: section.subject })));

export default function ProductUpdatePublishedEditor({ publication, onSaved, onClose, onDirtyChange, onBusyChange }) {
  const [saved, setSaved] = React.useState(publication);
  const [items, setItems] = React.useState(() => contentItems(publication.content_json));
  const [image, setImage] = React.useState(publication.content_json.image || null);
  const [subjects, setSubjects] = React.useState([]);
  const [error, setError] = React.useState("");
  const [conflict, setConflict] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [confirmReload, setConfirmReload] = React.useState(false);
  const busyRef = React.useRef(false);
  const imageDirty = JSON.stringify(image) !== JSON.stringify(saved.content_json.image || null);
  const dirty = imageDirty || JSON.stringify(items) !== JSON.stringify(contentItems(saved.content_json));
  const invalid = items.some(item => !item.title.trim() || !item.text.trim() || item.text.length > 4000);
  React.useEffect(() => {
    let active = true;
    productUpdatesRequest("/catalog").then(result => { if (active) setSubjects(result.subjects); })
      .catch(failure => { if (active) setError(failure.message); });
    return () => { active = false; };
  }, []);
  React.useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  React.useEffect(() => () => { onDirtyChange(false); onBusyChange(false); }, [onDirtyChange, onBusyChange]);
  const change = (id, key, value) => setItems(current => current.map(item => item.item_id === id ? { ...item, [key]: value } : item));
  function markBusy(value) { busyRef.current = value; setBusy(value); onBusyChange(value); }
  async function save(event) {
    event.preventDefault();
    if (busyRef.current || invalid || conflict || !dirty) return;
    markBusy(true); setError("");
    try {
      const previous = contentItems(saved.content_json);
      const updated = await productUpdatesRequest(`/publications/${saved.id}/content`, { method: "PATCH", body: {
        version: saved.version,
        items: items.map(({ image: itemImage, ...item }) => JSON.stringify(itemImage || null) === JSON.stringify(previous.find(row => row.item_id === item.item_id)?.image || null)
          ? item : { ...item, image: itemImage || null }),
        ...(imageDirty ? { image } : {}),
      } });
      onDirtyChange(false); onSaved(updated);
    } catch (failure) {
      setError(failure.message); setConflict(failure.status === 409 || failure.status === 404);
    } finally { markBusy(false); }
  }
  async function reload() {
    if (busyRef.current) return;
    markBusy(true); setConfirmReload(false);
    try {
      const result = await productUpdatesRequest("/published");
      const latest = result.publications.find(row => row.id === saved.id);
      if (!latest) throw new Error("Cette publication n’est plus disponible dans le Journal. Annulez l’édition pour revenir à la liste.");
      setSaved(latest); setItems(contentItems(latest.content_json)); setImage(latest.content_json.image || null);
      setError(""); setConflict(false);
    } catch (failure) { setError(failure.message); } finally { markBusy(false); }
  }
  return <div className="pu-admin pu-journal-editor">
    <form onSubmit={save} aria-label="Modifier la publication">
      <fieldset disabled={busy} className="pu-admin-controls">
        <div className="pu-texts-with-image"><div className="pu-texts-column">
          {items.map(item => <div className="pu-item-editor" key={item.item_id}>
            <label>Sujet<select aria-label="Sujet" value={item.subject} onChange={event => change(item.item_id, "subject", event.target.value)}>
              {[...new Set([item.subject, ...subjects])].map(subject => <option key={subject}>{subject}</option>)}
            </select></label>
            <label>Titre<input required maxLength={80} value={item.title} onChange={event => change(item.item_id, "title", event.target.value)} /></label>
            <label>Texte<textarea aria-label="Texte" required maxLength={4000} rows={4} value={item.text} onChange={event => change(item.item_id, "text", event.target.value)} /></label>
            {item.image && <ProductUpdateImageEditor image={item.image} disabled={busy}
              onChange={value => change(item.item_id, "image", value)} />}
          </div>)}
        </div><ProductUpdateImageEditor image={image} onChange={setImage} disabled={busy} pending={imageDirty} /></div>
        {error && <p role="alert" className="pu-error">{error}</p>}
        {conflict && <button type="button" className="pu-button" onClick={() => dirty ? setConfirmReload(true) : void reload()}>Recharger la publication</button>}
        <div className="pu-actions">
          <button type="submit" className="pu-button pu-button--primary" disabled={invalid || !dirty || conflict}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
          <button type="button" className="pu-button" onClick={onClose}>Annuler</button>
        </div>
      </fieldset>
    </form>
    {confirmReload && <ConfirmationModal title="Recharger la publication ?" tone="warning" panelSize="narrow" cornerRadius="grain" summaryLayout="stack"
      description="Vos modifications non enregistrées seront abandonnées pour charger la version actuelle."
      confirmLabel="Recharger" onConfirm={() => void reload()} onCancel={() => setConfirmReload(false)} onClose={() => setConfirmReload(false)} />}
  </div>;
}
